import { Link, Navigate, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, StatCard } from '@/components/ui/Card';
import { BandBar, BarList } from '@/components/ui/Charts';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { Button } from '@/components/ui/Button';
import { downloadCsv } from '@/lib/csv';
import { PageHeader } from '@/app/PlaceholderPage';
import { usePeriod } from '@/app/PeriodSwitcher';
import { useAuth } from '@/features/auth/AuthContext';
import { analyticsApi } from './api';

/**
 * Beranda hanya menampilkan 10 teratas.
 *
 * Uji beban dengan 64 TBM membuat halaman ini setinggi 5.300px dan didominasi
 * satu daftar peringkat — KPI di atasnya jadi tidak terlihat sama sekali.
 * Daftar penuhnya ada di Perbandingan & Tren, yang memang dirancang untuk itu.
 */
const RANKING_LIMIT = 10;

/**
 * Kategori yang paling banyak dihuni. Dipilih daripada "kategori tertinggi"
 * karena satu TBM yang menonjol tidak menggambarkan keadaan angkatan — yang
 * dicari koordinator adalah di mana mayoritasnya berada.
 * Bila seri, yang menang adalah kategori lebih tinggi: API sudah mengurutkan
 * bands dari ambang tertinggi.
 */
function modalBand(bands: { name: string; count: number }[]) {
  return bands.reduce<{ name: string; count: number } | null>(
    (best, b) => (best === null || b.count > best.count ? b : best),
    null,
  );
}

/** L-01 — Beranda: ringkasan eksekutif periode berjalan. */
export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { current, currentId, isAll } = usePeriod();

  if (user?.role === 'pelita' && user.individualId) {
    return <Navigate to={`/database/pelita/${user.individualId}`} replace />;
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', 'summary', currentId],
    queryFn: () => analyticsApi.summary(isAll ? 'all' : (current?.id ?? undefined)),
    enabled: Boolean(currentId),
  });

  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Beranda"
        description={isAll ? 'Ringkasan eksekutif seluruh semester' : `Ringkasan periode ${current?.name ?? '—'}`}
        action={
          data &&
          data.ranking.length > 0 && (
            <Button
              variant="secondary"
              onClick={() =>
                downloadCsv(`ringkasan-${isAll ? 'semua-semester' : (current?.name ?? 'periode')}.csv`, [
                  ['Kode', 'Nama TBM', 'Tahun program', 'Indeks', 'Kategori'],
                  ...data.ranking.map((r) => [
                    r.code,
                    r.name,
                    r.programYear ?? '',
                    r.index === null ? '' : Math.round(r.index * 10) / 10,
                    r.band ?? '',
                  ]),
                ])
              }
            >
              Unduh CSV
            </Button>
          )
        }
      />

      {isLoading || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : data.tbmAssessed === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="📊"
            title="Belum ada penilaian terkirim di periode ini"
            description="Angka di beranda hanya menghitung penilaian yang sudah dikirim atau diverifikasi — draf sengaja tidak ikut, supaya rata-rata tidak bergerak setiap kali seseorang mengetuk satu skor."
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Indeks rata-rata"
              value={data.meanIndex === null ? '—' : Math.round(data.meanIndex * 10) / 10}
              hint={`dari ${data.tbmAssessed} TBM yang dinilai`}
            />
            <StatCard
              label="TBM dinilai"
              value={`${data.tbmAssessed}/${data.tbmTotal}`}
              hint={data.tbmPending > 0 ? `${data.tbmPending} belum masuk` : 'semua sudah masuk'}
              tone={data.tbmPending > 0 ? 'warn' : 'success'}
            />
            <StatCard
              label="Temuan terbuka"
              value={data.findings.open + data.findings.inProgress}
              hint={`${data.findings.resolved} sudah selesai`}
              tone={data.findings.open > 0 ? 'warn' : 'default'}
            />
            <StatCard
              label="Kategori terbanyak"
              value={modalBand(data.bands)?.name ?? '—'}
              hint={modalBand(data.bands) ? `${modalBand(data.bands)?.count} dari ${data.tbmAssessed} TBM` : undefined}
            />
          </div>

          <Card>
            <h2 className="mb-3 text-[17px] font-semibold">Sebaran kategori</h2>
            <BandBar bands={data.bands} />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <h2 className="mb-3 text-[17px] font-semibold">Rata-rata per wilayah</h2>
              <BarList
                items={data.regions.map((r) => ({
                  label: r.name,
                  value: r.mean,
                  hint: `· ${r.count} TBM`,
                }))}
              />
            </Card>

            <Card padded={false}>
              <h2 className="px-5 pt-5 pb-3 text-[17px] font-semibold">
                Peringkat TBM
                {data.ranking.length > RANKING_LIMIT && (
                  <span className="ml-2 text-[13px] font-normal text-ink-muted">
                    10 teratas dari {data.ranking.length}
                  </span>
                )}
              </h2>
              <ul className="divide-y divide-[color:var(--color-border)]">
                {data.ranking.slice(0, RANKING_LIMIT).map((r, i) => (
                  <li key={r.tbmId}>
                    <button
                      onClick={() => navigate(`/analitik/tbm?tbm=${r.tbmId}`)}
                      className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-app"
                    >
                      <span className="tabular w-6 shrink-0 text-sm text-ink-muted">{i + 1}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-ink">{r.name}</span>
                        <span className="tabular text-[13px] text-ink-secondary">
                          {r.code} · Tahun {r.programYear ?? '—'}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="tabular block font-semibold text-ink">
                          {r.index === null ? '—' : Math.round(r.index * 10) / 10}
                        </span>
                        <span className="text-[12px] text-primary">{r.band ?? '—'}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {data.ranking.length > RANKING_LIMIT && (
                <div className="border-t border-border px-5 py-3">
                  <Link
                    to="/analitik/perbandingan"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Lihat peringkat lengkap →
                  </Link>
                </div>
              )}
            </Card>
          </div>

          <Card>
            <p className="text-[13px] text-ink-secondary">
              Seluruh angka di halaman ini dihitung dari penilaian berstatus{' '}
              <strong>Terkirim</strong> atau <strong>Terverifikasi</strong>. Indeks yang ditampilkan
              adalah angka yang dibekukan saat pengiriman — menyunting instrumen setelahnya tidak
              menggeser catatan lama.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
